import React, { useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import ProductForm from '../ProductForm/index';
import { useProductSelectionStep, ProductItem, Product } from './ProductSelectionStep.hooks';
import { useAsyncPaginatedCatalog } from '../../../../hooks/useAsyncPaginatedCatalog';
import { fetchActiveSuppliersPage } from '../../../../utils/catalogPageFetchers';
import {
  ProductSelectionContainer,
  ProductFormsContainer,
  AddButton,
  AddButtonIcon,
  ValidationStats,
  ValidationTitle,
  ErrorState,
  ErrorMessage,
  SupplierSelectorContainer,
} from './ProductSelectionStep.styles';

export interface Supplier {
  id: number;
  name: string;
  status?: string;
}

export interface ProductSelectionStepProps {
  productItems: ProductItem[];
  products: Product[];
  /** Cuando el panel está en paso 1: habilita carga paginada de proveedores. */
  supplierCatalogIsActive: boolean;
  /** Proveedor del registro en edición (para mostrar etiqueta aunque no esté en la primera página). */
  supplierEditPin?: { id: number; name: string } | null;
  selectedSupplierId: string;
  /** Muestra error en rojo solo tras validación (p. ej. guardar borrador sin proveedor). */
  showSupplierError?: boolean;
  onSupplierChange: (supplierId: string) => void;
  onAddRow: () => void;
  onRemoveRow: (id: number) => void;
  onUpdateItem: (id: number, field: keyof ProductItem, value: string) => void;
  disabled?: boolean;
}

const ProductSelectionStep: React.FC<ProductSelectionStepProps> = ({
  productItems,
  products,
  supplierCatalogIsActive,
  supplierEditPin = null,
  selectedSupplierId,
  showSupplierError = false,
  onSupplierChange,
  onAddRow,
  onRemoveRow,
  onUpdateItem,
  disabled = false,
}) => {
  const {
    safeProducts,
    getSelectedProductIds,
    canRemoveItem,
    hasProductItems,
    handleAddRow,
    handleRemoveRow,
    handleUpdateItem,
    validationStats,
  } = useProductSelectionStep({
    productItems,
    products,
    onAddRow,
    onRemoveRow,
    onUpdateItem,
  });

  const supplierPins = useMemo(() => {
    const id = Number(selectedSupplierId);
    if (!id || Number.isNaN(id)) return [];
    if (supplierEditPin && supplierEditPin.id === id) {
      return [{ id: supplierEditPin.id, name: supplierEditPin.name }];
    }
    return [{ id, name: `Proveedor #${id}` }];
  }, [selectedSupplierId, supplierEditPin]);

  const supplierCatalog = useAsyncPaginatedCatalog({
    isActive: supplierCatalogIsActive,
    fetchPage: fetchActiveSuppliersPage,
    pageSize: 10,
    pinItems: supplierPins,
  });

  const selectedSupplier =
    supplierCatalog.displayOptions.find((s) => s.id.toString() === selectedSupplierId) || null;

  const isSupplierMissing = !selectedSupplierId || selectedSupplierId === '';
  const showSupplierFieldError = showSupplierError && isSupplierMissing && !disabled;

  return (
    <ProductSelectionContainer>
      {disabled && (
        <ErrorState style={{ marginBottom: '16px' }}>
          <ErrorMessage style={{ color: 'inherit', fontSize: '0.8125rem' }}>
            No se pueden modificar los productos en un registro pendiente
          </ErrorMessage>
        </ErrorState>
      )}

      {/* Supplier Selector */}
      <SupplierSelectorContainer>
        <Autocomplete
          options={supplierCatalog.displayOptions}
          filterOptions={supplierCatalog.filterOptions}
          loading={supplierCatalog.loading}
          openOnFocus
          onOpen={supplierCatalog.onOpen}
          onInputChange={supplierCatalog.onInputChange}
          getOptionLabel={(option) => option.name}
          value={selectedSupplier || null}
          onChange={(_, newValue) => {
            onSupplierChange(newValue ? newValue.id.toString() : '');
          }}
          disabled={disabled}
          ListboxProps={supplierCatalog.listboxProps}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Proveedor"
              placeholder="Seleccionar proveedor"
              required
              error={showSupplierFieldError}
              helperText={
                showSupplierFieldError
                  ? 'Debe seleccionar un proveedor'
                  : !disabled && isSupplierMissing
                    ? 'Campo obligatorio'
                    : ''
              }
              FormHelperTextProps={
                !showSupplierFieldError && !disabled && isSupplierMissing
                  ? { sx: { color: 'text.secondary' } }
                  : undefined
              }
            />
          )}
          noOptionsText="No hay proveedores disponibles"
        />
      </SupplierSelectorContainer>

      <ProductFormsContainer>
        {productItems.map((item, index) => (
          <ProductForm
            key={item.id}
            item={item}
            index={index}
            products={safeProducts}
            canRemove={canRemoveItem && !disabled}
            onRemove={handleRemoveRow}
            onUpdate={handleUpdateItem}
            selectedProductIds={getSelectedProductIds(item.id)}
            disabled={disabled}
            supplierId={selectedSupplierId}
          />
        ))}
      </ProductFormsContainer>

      <AddButton
        onClick={handleAddRow}
        aria-label="Add another product"
        disabled={disabled || isSupplierMissing}
      >
        <AddButtonIcon
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </AddButtonIcon>
        Agregar producto
      </AddButton>

      {/* Contador de productos */}
      {hasProductItems && (
        <ValidationStats>
          <ValidationTitle>
            {validationStats.total}{' '}
            {validationStats.total === 1 ? 'producto seleccionado' : 'productos seleccionados'}
          </ValidationTitle>
        </ValidationStats>
      )}

      {/* Estado de error si no hay items */}
      {!hasProductItems && (
        <ErrorState>
          <ErrorMessage>Debes agregar al menos un producto para continuar</ErrorMessage>
        </ErrorState>
      )}
    </ProductSelectionContainer>
  );
};

export default ProductSelectionStep;
